import {adjustmentsSchema} from './document-adjustments';
import { z } from 'zod';
import {templateSchema} from './document-masters';
import { isoDate } from './fiscal-model';
import { invoiceAmounts } from './invoice-posting-model';
import type { Party } from './types';

const text = z.string().max(2000).nullable();
const amount = z.number().finite().nonnegative().max(999999999999).nullable();
/** Extraction is evidence, never an instruction to create masters or post entries. */
export const billExtractionSchema = z.object({
  documentCount: z.number().int().min(0).max(100),
  vendor: z.object({ name: text, gstin: text, pan: text, address: text, state: text, country: text }).strict(),
  invoiceNumber: text, date: isoDate.nullable(), dueDate: isoDate.nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable(), placeOfSupply: text,
  subtotal: amount, tax: amount, total: amount, discount: amount,
  adjustment: z.number().finite().min(-999999999999).max(999999999999).nullable(),
  notes: text,
  lines: z.array(z.object({
    item: text, quantity: amount, rate: amount, taxRate: z.number().min(0).max(100).nullable(),
    hsnSac: text, discount: amount, amount: amount
  }).strict()).max(200),
  uncertainFields: z.array(z.string().max(200)).max(200),
  warnings: z.array(z.string().max(500)).max(50)
}).strict();
export type BillExtraction = z.infer<typeof billExtractionSchema>;
export interface OcrResult { documentId: string; name: string; extraction: BillExtraction; warnings: string[]; reused?: boolean }

export function matchBillVendor(vendor: BillExtraction['vendor'], parties: Party[]) {
  const pool = parties.filter(p => p.type === 'Vendor' && p.active);
  const gstin = vendor.gstin?.replace(/\s/g, '').toUpperCase();
  // A supplied tax ID must never fall back to a conflicting name-only match.
  if (gstin) return pool.filter(p => p.gstin?.replace(/\s/g, '').toUpperCase() === gstin);
  const name = vendor.name?.trim().toLowerCase();
  return name ? pool.filter(p => p.name.trim().toLowerCase() === name) : [];
}

export function extractionWarnings(data: BillExtraction): string[] {
  const warnings = [...data.warnings];
  if (data.documentCount !== 1) warnings.push('Upload exactly one supplier bill at a time.');
  for (const [field, value] of Object.entries({ vendor: data.vendor.name, invoiceNumber: data.invoiceNumber, date: data.date, currency: data.currency, total: data.total })) {
    if (value === null || value === '') warnings.push(`Missing ${field}: enter it from the source document.`);
  }
  if (!data.lines.length) warnings.push('No line items extracted. Enter them from the source document.');
  if (data.uncertainFields.length) warnings.push(`Verify uncertain fields: ${data.uncertainFields.join(', ')}.`);
  if (data.discount || data.adjustment || data.lines.some(l => l.discount)) warnings.push('Discounts/adjustments need manual review; gross rates are not silently changed.');
  try {
    if (data.lines.some(l => l.quantity === null || l.rate === null || l.taxRate === null)) warnings.push('Some quantity, price or tax values are missing.');
    else {
      const totals = invoiceAmounts(data.lines.map(l => ({ quantity: String(l.quantity), rate: String(l.rate), taxRate: String(l.taxRate), taxTreatment: 'Regular' })));
      for (const field of ['subtotal', 'tax', 'total'] as const) {
        if (data[field] !== null && Math.abs(Number(totals[field]) - data[field]!) > 0.01) warnings.push(`Calculated ${field} does not match the printed ${field}. Review before saving.`);
      }
    }
  } catch { warnings.push('Some extracted amounts need correction before they can be calculated.'); }
  return [...new Set(warnings)];
}

export const draftDetailsSchema = z.object({
 adjustments:adjustmentsSchema.optional(),
  tdsRuleId:z.string().max(100).optional(),
  templateSnapshot:templateSchema.optional(),
  paymentTermId: z.string().max(100).optional(),
  paymentTermDays: z.number().int().min(0).max(3650).optional(),
  customFields: z.array(z.object({id:z.string().max(100),label:z.string().max(200),value:z.string().max(2000),print:z.boolean()}).strict()).max(100).optional(),
  supplierInvoiceNumber: z.string().trim().max(100).optional(),
  dueDate: isoDate.optional(), template: z.string().max(200).optional(), notes: z.string().max(4000).optional(),
  sourceDocumentId: z.string().max(100).optional(), ocrReviewed: z.boolean().optional(),
  ocrReviewReason: z.string().trim().max(500).optional()
}).strict();
export type DraftDetails = z.infer<typeof draftDetailsSchema>;
